import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { X, ChevronDown, ChevronUp, LogIn, ThumbsUp, Users, Mail, ShoppingCart, ChevronRight, Copy, Gift, Sparkles, Flame } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import { useAuth } from "@/contexts/AuthContext";
import { usePayment } from "@/hooks/usePayment";
import { useAvailablePaymentMethods } from "@/hooks/useAvailablePaymentMethods";
import { useDailyBonus } from "@/contexts/DailyBonusContext";
import { useDailyBonusStatus } from "@/hooks/useDailyBonus";
import { toast } from "sonner";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useState, useEffect, useRef, useCallback } from "react";
import { apiClient } from "@/lib/api-client";
import { copyToClipboard } from "@/lib/clipboard";
import { RegularPackageCard } from "@/components/RegularPackageCard";
import { CompactOfferCard } from "@/components/CompactOfferCard";
import { CompactGroupOfferCard } from "@/components/CompactGroupOfferCard";
import { GCBadgeSVG, SCBadgeSVG } from "@/components/CurrencyBadge";
import { generateReferralLink } from "@/lib/referral-utils";
import { cn } from "@/lib/utils";

// Loading skeleton for the Get Coins page
const GetCoinsSkeleton = () => (
  <div className="space-y-5 lg:space-y-6">
    {/* Today's Offers Section Skeleton */}
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Skeleton className="h-5 w-5 rounded bg-orange-500/20" />
        <Skeleton className="h-5 w-28 bg-muted/40" />
        <Skeleton className="h-5 w-16 rounded-full bg-orange-500/20" />
      </div>
      <div className="flex gap-4 overflow-hidden pb-3">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="flex-shrink-0 w-[230px] lg:w-[265px]">
            <div className="bg-muted/50 rounded-xl border border-border p-4 h-[250px]">
              <Skeleton className="h-4 w-20 bg-muted/30 mb-3" />
              <Skeleton className="h-8 w-full bg-muted/20 mb-2" />
              <Skeleton className="h-6 w-3/4 bg-muted/20 mb-4" />
              <Skeleton className="h-10 w-full rounded-lg bg-muted/30" />
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Rewards Section Skeleton (collapsed) */}
    <div className="bg-muted/40 border border-border rounded-xl">
      <div className="px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-4 rounded bg-amber-500/20" />
          <Skeleton className="h-4 w-40 bg-muted/40" />
        </div>
        <Skeleton className="h-4 w-4 bg-muted/30" />
      </div>
    </div>

    {/* Promo Code Section Skeleton (collapsed) */}
    <div className="bg-muted/40 border border-border rounded-xl">
      <div className="px-4 py-3 flex items-center justify-between">
        <Skeleton className="h-4 w-36 bg-muted/40" />
        <Skeleton className="h-4 w-4 bg-muted/30" />
      </div>
    </div>

    {/* Divider Skeleton */}
    <div className="flex items-center gap-3">
      <div className="flex-1 h-px bg-gradient-to-r from-transparent via-slate-700 to-transparent" />
      <Skeleton className="h-3 w-24 bg-muted/30" />
      <div className="flex-1 h-px bg-gradient-to-r from-transparent via-slate-700 to-transparent" />
    </div>

    {/* Regular Packages Section Skeleton */}
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Skeleton className="h-5 w-5 rounded-full bg-amber-500/20" />
          <Skeleton className="h-5 w-32 bg-muted/40" />
          <Skeleton className="h-4 w-8 bg-muted/30" />
        </div>
        <Skeleton className="h-8 w-32 rounded-full bg-muted/20" />
      </div>
      
      {/* Desktop Grid */}
      <div className="hidden lg:grid lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="bg-muted/50 rounded-xl border border-border p-4 h-[120px]">
            <div className="flex items-center gap-2 mb-3">
              <Skeleton className="h-6 w-6 rounded bg-muted/30" />
              <Skeleton className="h-5 w-20 bg-muted/40" />
            </div>
            <Skeleton className="h-4 w-16 bg-muted/30 mb-2" />
            <Skeleton className="h-8 w-full rounded-lg bg-muted/20" />
          </div>
        ))}
      </div>
      
      {/* Mobile Grid */}
      <div className="lg:hidden grid grid-cols-2 gap-2.5">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="bg-muted/50 rounded-xl border border-border p-3 h-[100px]">
            <div className="flex items-center gap-2 mb-2">
              <Skeleton className="h-5 w-5 rounded bg-muted/30" />
              <Skeleton className="h-4 w-16 bg-muted/40" />
            </div>
            <Skeleton className="h-3 w-12 bg-muted/30 mb-2" />
            <Skeleton className="h-7 w-full rounded-lg bg-muted/20" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

interface CoinPackage {
  id: string;
  title: string;
  gc_amount: number;
  sc_amount: number;
  gc_bonus_amount: number;
  sc_bonus_amount: number;
  price: number;
  original_price: number;
  bonus_or_discount_valid_until?: string;
  package_valid_until?: string;
  is_special_offer: boolean | string;
  is_active: boolean;
  display_order: number;
  is_welcome_offer?: boolean;
  // Promo code applied fields
  promo_code?: string;
  promo_reward_type?: string;
  promo_reward_value?: number;
  promo_valid_until?: string;
  promo_remaining_uses?: number;
  promo_bonus_gc?: number;
  promo_bonus_sc?: number;
  total_gc?: number;
  total_sc?: number;
  discounted_price?: number;
}

// Scheduled offer types
interface ScheduledOffer {
  id: number;
  title: string;
  gc_amount: number;
  sc_amount: number;
  gc_bonus_amount: number;
  sc_bonus_amount: number;
  price: number;
  original_price: number | null;
  cadence_type: string;
  resets_at: string | null;
  can_purchase: boolean;
  purchases_remaining: number;
  group_id: number | null;
  is_welcome_offer: boolean;
}

interface ScheduledOfferGroup {
  id: number;
  title: string;
  offers: ScheduledOffer[];
  is_welcome_group: boolean;
}

interface ScheduledOffersData {
  groups: ScheduledOfferGroup[];
  ungrouped: ScheduledOffer[];
}

/**
 * Unified purchase item type that works for all package/offer types.
 * This allows us to pass the full object instead of searching by title.
 */
export interface PurchaseItem {
  id: string | number;
  title: string;
  price: number;
  original_price?: number | null;
  gc_amount: number;
  sc_amount: number;
  gc_bonus_amount: number;
  sc_bonus_amount: number;
  /** Type of item being purchased */
  type: 'coin_package' | 'scheduled_offer' | 'promo_offer' | 'special_offer';
  // Promo code fields (only for promo offers)
  promo_code?: string;
  promo_reward_type?: string;
  promo_reward_value?: number;
  promo_bonus_gc?: number;
  promo_bonus_sc?: number;
  discounted_price?: number;
}

export interface GetCoinsContentProps {
  onClose: () => void;
  /** Whether this is rendered as a page (vs modal). Affects header styling. */
  isPage?: boolean;
}

export const GetCoinsContent = ({ onClose, isPage = false }: GetCoinsContentProps) => {
  const { selectedCurrency, getThemeColors } = useTheme();
  const { user } = useAuth();
  const { openPayment } = usePayment();
  const { coinflowOnly } = useAvailablePaymentMethods();
  
  const [packages, setPackages] = useState<CoinPackage[]>([]);
  const [scheduledOffers, setScheduledOffers] = useState<ScheduledOffersData>({ groups: [], ungrouped: [] });
  const [loading, setLoading] = useState(true);
  const contentRef = useRef<HTMLDivElement>(null);
  const themeColors = getThemeColors();
  
  // Promo code state
  const [promoCodeInput, setPromoCodeInput] = useState("");
  const [applyingPromoCode, setApplyingPromoCode] = useState(false);
  const [promoOffers, setPromoOffers] = useState<CoinPackage[]>([]);
  const [appliedPromoCode, setAppliedPromoCode] = useState<string | null>(null);
  const [showPromoCodeSection, setShowPromoCodeSection] = useState(false);
  
  // SC Info modal state
  const [showSCInfoModal, setShowSCInfoModal] = useState(false);
  
  // Rewards section state
  const [showRewardsSection, setShowRewardsSection] = useState(false);
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [referralRewards, setReferralRewards] = useState<{ gc: number; sc: number } | null>(null);
  const [referralLoading, setReferralLoading] = useState(false);
  const [referralDataFetched, setReferralDataFetched] = useState(false);
  
  // Daily bonus hooks
  const { openDailyBonusModal, canClaim: dailyBonusCanClaim } = useDailyBonus();
  const { status: dailyBonusStatus } = useDailyBonusStatus();

  const callCoinsOperation = async (action: string, params: any = {}) => {
    try {
      let data;
      // Use new REST endpoints
      switch (action) {
        case 'getPackages':
          data = await apiClient.get('/coins/packages');
          break;
        case 'getBalance':
          data = await apiClient.get('/coins/balance');
          break;
        case 'getTransactions':
          data = await apiClient.get('/coins/transactions');
          break;
        default:
          throw new Error(`Unknown coins action: ${action}`);
      }
      
      // Backend returns data directly without success wrapper
      // getPackages returns { packages }
      // getBalance returns { balance }
      // getTransactions returns { transactions }
      return data;
    } catch (error: any) {
      toast.error(error.message || 'Operation failed');
      throw error;
    }
  };

  /**
   * Handle purchase - accepts full item object instead of searching by title
   */
  const handlePurchase = async (item: PurchaseItem) => {
    const isScheduledOffer = item.type === 'scheduled_offer';

    // If coinflowOnly is true from the start, close the drawer immediately
    // (the Coinflow checkout will take over the full screen)
    // Otherwise, keep the drawer open underneath for the back button to return to
    if (coinflowOnly) {
      onClose();
    }

    // Open payment via PaymentProvider (handles verification + geocomply checks)
    await openPayment(
      {
        id: item.id.toString(),
        title: item.title,
        price: item.discounted_price !== undefined ? item.discounted_price : item.price,
        originalPrice: item.original_price ?? undefined,
        gcAmount: item.gc_amount,
        scAmount: item.sc_amount,
        gcBonusAmount: item.gc_bonus_amount,
        scBonusAmount: item.sc_bonus_amount,
        promoCode: item.promo_code,
        promoRewardType: item.promo_reward_type,
        promoRewardValue: item.promo_reward_value,
        promoBonusGC: item.promo_bonus_gc,
        promoBonusSC: item.promo_bonus_sc,
        isScheduledOffer,
        coinflowOnly,  // Dynamic: true when only coinflow_hosted is enabled
      },
      {
        onSuccess: () => {
          // Close the drawer after successful payment (if not already closed for exclusive mode)
          if (!coinflowOnly) {
            onClose();
          }
          // Refresh packages/offers immediately to update eligibility (e.g., remove purchased offers from groups)
          fetchPackages();
        },
        // onBack doesn't need to do anything - drawer is still open underneath (unless exclusive mode)
        onSwitchToCoinflowOnly: () => {
          // Close the drawer when switching to Coinflow-only mode (Coinflow takes over)
          onClose();
        },
      }
    );
  };

  const handleApplyPromoCode = async () => {
    if (!promoCodeInput.trim() || !user) {
      toast.error('Please enter a promo code');
      return;
    }

    setApplyingPromoCode(true);
    try {
      const result = await apiClient.post('/promo-codes/apply', { code: promoCodeInput.trim() });
      if (result.success && result.packages) {
        toast.success('Promo code applied successfully!');
        setAppliedPromoCode(result.promoCode?.code || promoCodeInput.trim().toUpperCase());
        setPromoOffers(result.packages || []);
        setPromoCodeInput("");
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to apply promo code');
    } finally {
      setApplyingPromoCode(false);
    }
  };

  const fetchPackages = useCallback(async () => {
    try {
      const result = await callCoinsOperation('getPackages');
      setPackages(result.packages || []);
      // Handle scheduled offers if returned
      if (result.scheduledOffers) {
        setScheduledOffers(result.scheduledOffers);
      }
    } catch {
      setPackages([]);
      setScheduledOffers({ groups: [], ungrouped: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPackages();
  }, [fetchPackages]);

  // Subscribe to balance updates to refresh offers immediately after any purchase
  // This ensures "pick one" group offers and purchase limits are correctly updated
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    // Dynamic import to avoid SSR issues
    import('@/lib/websocket-client').then(({ websocketClient }) => {
      unsubscribe = websocketClient.subscribe('balance:update', () => {
        fetchPackages();
      });
    });

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [fetchPackages]);

  // Fetch referral data only when rewards section is expanded (lazy loading)
  useEffect(() => {
    const fetchReferralData = async () => {
      if (!user || !showRewardsSection || referralDataFetched) return;

      setReferralLoading(true);
      try {
        const data = await apiClient.get('/referral/summary');
        if (data.success) {
          setReferralCode(data.referralCode || null);
          setReferralRewards({
            gc: Number(data.maxGcReward) || 0,
            sc: Number(data.maxScReward) || 0,
          });
        }
        setReferralDataFetched(true);
      } catch {
        // Error fetching referral data
      } finally {
        setReferralLoading(false);
      }
    };

    fetchReferralData();
  }, [user, showRewardsSection, referralDataFetched]);

  const formatPrice = (price: number | string | null | undefined): string => {
    const numPrice = typeof price === 'string' ? parseFloat(price) : (price || 0);
    if (isNaN(numPrice)) return '$0.00';
    return `$${numPrice.toFixed(2)}`;
  };

  const formatCoins = (amount: number | string | null | undefined): string => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : (amount || 0);
    if (!numAmount || isNaN(numAmount)) return '0';
    if (numAmount >= 1000000) {
      // Show one decimal place for millions (e.g., 1.5M)
      const millions = Math.floor(numAmount / 100000) / 10;
      return `${millions}M`;
    } else if (numAmount >= 10000) {
      // Show one decimal place for 10K+ (e.g., 218.5K)
      const thousands = Math.floor(numAmount / 100) / 10;
      return `${thousands}K`;
    } else if (numAmount >= 1000) {
      // Show one decimal place for 1K+ (e.g., 1.5K)
      const thousands = Math.floor(numAmount / 100) / 10;
      return `${thousands}K`;
    }
    // For small numbers, preserve decimals if they exist (important for SC like 1.12)
    // Show up to 2 decimal places, remove trailing zeros
    if (numAmount % 1 !== 0) {
      return parseFloat(numAmount.toFixed(2)).toString();
    }
    return numAmount.toLocaleString();
  };

  const getPackageIcon = (gcAmount: number, scAmount: number): string => {
    const totalValue = gcAmount + (scAmount * 1000); // Rough value calculation
    
    if (totalValue >= 5000000) return "👑"; // Crown for VIP
    if (totalValue >= 1000000) return "⭐"; // Star for Ultimate
    if (totalValue >= 500000) return "🏆"; // Trophy for Premium
    if (totalValue >= 100000) return "💎"; // Diamond for Value
    return "🎯"; // Target for Starter
  };

  const regularPackages = packages.filter(pkg => !pkg.is_special_offer || pkg.is_special_offer === false || pkg.is_special_offer === 'false');

  return (
    <>
      <div 
        ref={contentRef}
        className={cn(
          "flex flex-col bg-background",
          isPage ? "min-h-screen" : "max-h-[100vh] h-full"
        )}
      >
        {/* Close button - floating in corner (all screen sizes) */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="flex absolute right-3 top-[calc(env(safe-area-inset-top)+0.75rem)] lg:right-4 lg:top-4 z-20 h-9 w-9 lg:h-10 lg:w-10 rounded-full bg-muted/80 hover:bg-muted border border-border"
          aria-label="Close"
        >
          <X className="h-4 w-4 lg:h-5 lg:w-5 text-muted-foreground" />
        </Button>
          
        <div className={cn(
          "flex-1 overflow-y-auto min-h-0 pb-6 lg:pb-8 pt-4 lg:pt-6 bg-background",
          isPage ? "pb-[calc(5rem+env(safe-area-inset-bottom))]" : "pb-[calc(5rem+env(safe-area-inset-bottom))] lg:pb-0"
        )}>
          <div className="container mx-auto px-4 lg:px-6 space-y-5 lg:space-y-6 pb-4 max-w-full lg:max-w-6xl">

            {/* Show full page skeleton while loading */}
            {loading ? (
              <GetCoinsSkeleton />
            ) : (
            <>
            {/* Today's Offers Section - Scheduled + Groups */}
            {(scheduledOffers.ungrouped.length > 0 || scheduledOffers.groups.length > 0) && (
              <div className="space-y-3 todays-offers-slider">
                <div className="flex items-center gap-2">
                  <Flame className="h-5 w-5 text-orange-400" />
                  <h2 className="text-sm lg:text-base font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-400 uppercase tracking-wide">
                    Today's Offers
                  </h2>
                  <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30 text-[10px] font-bold">
                    {scheduledOffers.ungrouped.length + scheduledOffers.groups.length} deals
                  </Badge>
                </div>

                <div className="flex gap-1 overflow-x-auto scrollbar-hide [&>*]:pt-3 [&>*]:pb-2 [&>*]:px-1.5 -mx-4 lg:-mx-6 pl-3 lg:pl-5">
                      {/* Sort offers: Welcome first, then others */}
                      {(() => {
                        // Separate welcome offers from others
                        const welcomeGroups = scheduledOffers.groups.filter(g => 
                          g.is_welcome_group || g.offers.some(o => o.is_welcome_offer)
                        );
                        const otherGroups = scheduledOffers.groups.filter(g => 
                          !g.is_welcome_group && !g.offers.some(o => o.is_welcome_offer)
                        );
                        const welcomeUngrouped = scheduledOffers.ungrouped.filter(o => o.is_welcome_offer);
                        const otherUngrouped = scheduledOffers.ungrouped.filter(o => !o.is_welcome_offer);

                        return (
                          <>
                            {/* 1. Welcome group offers first */}
                            {welcomeGroups.map((group) => (
                              <div key={`group-${group.id}`} className="flex-shrink-0 w-[300px] lg:w-[330px]">
                                <CompactGroupOfferCard
                                  group={group}
                                  onPurchase={handlePurchase}
                                  formatCoins={formatCoins}
                                  formatPrice={formatPrice}
                                />
                              </div>
                            ))}
                            
                            {/* 2. Welcome ungrouped offers */}
                            {welcomeUngrouped.map((offer) => {
                              const totalGC = offer.gc_amount + offer.gc_bonus_amount;
                              const totalSC = offer.sc_amount + offer.sc_bonus_amount;
                              const canPurchase = offer.can_purchase !== false && (offer.purchases_remaining ?? 1) > 0;
                              
                              return (
                                <div key={`scheduled-${offer.id}`} className="flex-shrink-0 w-[230px] lg:w-[265px]">
                                  <CompactOfferCard
                                    type="scheduled"
                                    gcAmount={totalGC}
                                    scAmount={totalSC}
                                    gcBaseAmount={offer.gc_amount}
                                    scBaseAmount={offer.sc_amount}
                                    price={offer.price}
                                    originalPrice={offer.original_price}
                                    cadenceLabel="welcome"
                                    isWelcomeOffer={true}
                                    expiresAt={offer.resets_at}
                                    purchasesRemaining={offer.purchases_remaining}
                                    canPurchase={canPurchase}
                                    onPurchase={() => handlePurchase({
                                      id: offer.id,
                                      title: offer.title,
                                      price: offer.price,
                                      original_price: offer.original_price,
                                      gc_amount: offer.gc_amount,
                                      sc_amount: offer.sc_amount,
                                      gc_bonus_amount: offer.gc_bonus_amount,
                                      sc_bonus_amount: offer.sc_bonus_amount,
                                      type: 'scheduled_offer',
                                    })}
                                    formatCoins={formatCoins}
                                    formatPrice={formatPrice}
                                  />
                                </div>
                              );
                            })}
                            
                            {/* 3. Other group offers */}
                            {otherGroups.map((group) => (
                              <div key={`group-${group.id}`} className="flex-shrink-0 w-[300px] lg:w-[330px]">
                                <CompactGroupOfferCard
                                  group={group}
                                  onPurchase={handlePurchase}
                                  formatCoins={formatCoins}
                                  formatPrice={formatPrice}
                                />
                              </div>
                            ))}
                            
                            {/* 4. Other scheduled offers */}
                            {otherUngrouped.map((offer) => {
                              const totalGC = offer.gc_amount + offer.gc_bonus_amount;
                              const totalSC = offer.sc_amount + offer.sc_bonus_amount;
                              const canPurchase = offer.can_purchase !== false && (offer.purchases_remaining ?? 1) > 0;
                              
                              return (
                                <div key={`scheduled-${offer.id}`} className="flex-shrink-0 w-[230px] lg:w-[265px]">
                                  <CompactOfferCard
                                    type="scheduled"
                                    gcAmount={totalGC}
                                    scAmount={totalSC}
                                    gcBaseAmount={offer.gc_amount}
                                    scBaseAmount={offer.sc_amount}
                                    price={offer.price}
                                    originalPrice={offer.original_price}
                                    cadenceLabel={offer.cadence_type}
                                    isWelcomeOffer={offer.is_welcome_offer}
                                    expiresAt={offer.resets_at}
                                    purchasesRemaining={offer.purchases_remaining}
                                    canPurchase={canPurchase}
                                    onPurchase={() => handlePurchase({
                                      id: offer.id,
                                      title: offer.title,
                                      price: offer.price,
                                      original_price: offer.original_price,
                                      gc_amount: offer.gc_amount,
                                      sc_amount: offer.sc_amount,
                                      gc_bonus_amount: offer.gc_bonus_amount,
                                      sc_bonus_amount: offer.sc_bonus_amount,
                                      type: 'scheduled_offer',
                                    })}
                                    formatCoins={formatCoins}
                                    formatPrice={formatPrice}
                                  />
                                </div>
                              );
                            })}
                            
                          </>
                        );
                      })()}
                </div>
              </div>
            )}

            {/* Rewards & Refer Section - Collapsible */}
            {user && (
              <div className="bg-card border border-border rounded-xl overflow-hidden">
                {/* Collapsible Header */}
                <button
                  onClick={() => setShowRewardsSection(!showRewardsSection)}
                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-muted/60 transition-colors"
                >
                  <span className="text-sm font-semibold text-foreground flex items-center gap-2">
                    <Gift className="h-4 w-4 text-amber-400" />
                    Rewards & Refer A Friend
                    {dailyBonusCanClaim && (
                      <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-[10px] px-1.5 py-0.5">
                        Claim!
                      </Badge>
                    )}
                  </span>
                  {showRewardsSection ? (
                    <ChevronUp className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  )}
                </button>
                
                {/* Expanded Content */}
                {showRewardsSection && (
                  <div className="px-4 pb-4 border-t border-border pt-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {/* Refer A Friend Card */}
                      <div className="bg-gradient-to-br from-amber-950/60 via-amber-900/40 to-amber-800/30 rounded-xl border border-amber-500/30 p-4 relative overflow-hidden">
                        {/* Decorative gradient */}
                        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500" />
                        
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <h4 className="text-sm font-bold text-foreground mb-1">Refer A Friend</h4>
                            <p className="text-xs text-muted-foreground mb-2">Earn up to per friend:</p>
                            
                            {/* Rewards display */}
                            {referralRewards && (referralRewards.gc > 0 || referralRewards.sc > 0) && (
                              <div className="flex items-center gap-2 mb-3">
                                <div className="flex items-center gap-1">
                                  <GCBadgeSVG size={20} />
                                  <span className="text-sm font-bold text-amber-300">
                                    {referralRewards.gc >= 1000 ? `${(referralRewards.gc / 1000).toFixed(0)}K` : referralRewards.gc.toLocaleString()}
                                  </span>
                                </div>
                                <span className="text-muted-foreground/70 text-xs">+</span>
                                <div className="flex items-center gap-1">
                                  <SCBadgeSVG size={20} />
                                  <span className="text-sm font-bold text-green-400">{referralRewards.sc.toLocaleString()}</span>
                                  <span className="text-[10px] text-green-400/70">FREE</span>
                                </div>
                              </div>
                            )}
                          </div>
                          
                          {/* Icon area */}
                          <div className="w-12 h-12 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                            <Users className="h-6 w-6 text-amber-400" />
                          </div>
                        </div>
                        
                        {/* Copy Link Button */}
                        <Button
                          onClick={async () => {
                            if (referralCode) {
                              const link = generateReferralLink(referralCode);
                              const success = await copyToClipboard(link);
                              if (success) {
                                toast.success('Referral link copied to clipboard!');
                              } else {
                                toast.error('Failed to copy to clipboard');
                              }
                            }
                          }}
                          disabled={!referralCode || referralLoading}
                          className="w-full h-9 bg-gradient-to-r from-amber-500 to-yellow-400 hover:from-amber-400 hover:to-yellow-300 text-amber-900 font-bold text-sm rounded-lg"
                        >
                          <Copy className="h-4 w-4 mr-2" />
                          Copy Link
                        </Button>
                      </div>
                      
                      {/* Daily Bonus Card */}
                      <div className={cn(
                        "rounded-xl border p-4 relative overflow-hidden",
                        dailyBonusStatus?.purchaseRequirement?.required
                          ? "bg-gradient-to-br from-amber-950/60 via-amber-900/40 to-amber-800/30 border-amber-500/30"
                          : "bg-gradient-to-br from-green-950/60 via-emerald-900/40 to-green-800/30 border-green-500/30"
                      )}>
                        {/* Decorative gradient */}
                        <div className={cn(
                          "absolute top-0 left-0 right-0 h-1",
                          dailyBonusStatus?.purchaseRequirement?.required
                            ? "bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500"
                            : "bg-gradient-to-r from-green-500 via-emerald-400 to-green-500"
                        )} />
                        
                        {dailyBonusStatus?.purchaseRequirement?.required ? (
                          /* Purchase Required State */
                          <>
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1">
                                <h4 className="text-sm font-bold text-foreground mb-1">Daily Bonus</h4>
                                <p className="text-xs text-muted-foreground mb-3">Unlock with any purchase</p>
                                <p className="text-xs text-amber-400/80 leading-relaxed">
                                  Any purchase unlocks daily bonus for <span className="font-semibold">{dailyBonusStatus?.purchaseRequirement?.unlockDays ?? 14} days</span>. Each purchase extends access!
                                </p>
                              </div>
                              
                              {/* Icon area */}
                              <div className="w-12 h-12 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                                <Gift className="h-6 w-6 text-amber-400" />
                              </div>
                            </div>
                            
                            {/* Alternative AMOE link */}
                            <div className="mt-3 pt-3 border-t border-amber-500/20">
                              <a 
                                href="/amoe" 
                                className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1"
                              >
                                Or request free SC via mail (AMOE)
                                <ChevronRight className="h-3 w-3" />
                              </a>
                            </div>
                          </>
                        ) : (
                          /* Normal Daily Bonus State */
                          <>
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <h4 className="text-sm font-bold text-foreground">
                                    {dailyBonusStatus?.streak?.currentCycleDay 
                                      ? `Day ${dailyBonusStatus.streak.currentCycleDay}` 
                                      : 'Daily'}
                                  </h4>
                                  {dailyBonusStatus?.vipTier && dailyBonusStatus.vipTier.multiplier > 1 && (
                                    <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 text-[10px] px-1.5 py-0.5">
                                      {dailyBonusStatus.vipTier.multiplier}x VIP
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-xs text-muted-foreground mb-3">Free Rewards</p>
                                
                                {/* Rewards display */}
                                {dailyBonusStatus?.todayReward && (
                                  <div className="flex items-center gap-2 mb-3">
                                    <div className="flex items-center gap-1">
                                      <GCBadgeSVG size={20} />
                                      <span className="text-xs font-bold text-amber-300">
                                        {dailyBonusStatus.todayReward.gc >= 1000 
                                          ? `${(dailyBonusStatus.todayReward.gc / 1000).toFixed(0)}K` 
                                          : dailyBonusStatus.todayReward.gc}
                                      </span>
                                    </div>
                                    <span className="text-muted-foreground/70 text-xs">+</span>
                                    <div className="flex items-center gap-1">
                                      <SCBadgeSVG size={20} />
                                      <span className="text-xs font-bold text-green-400">
                                        {dailyBonusStatus.todayReward.sc}
                                      </span>
                                      <span className="text-[10px] text-green-400/70">FREE</span>
                                    </div>
                                  </div>
                                )}
                              </div>
                              
                              {/* Icon area */}
                              <div className="w-12 h-12 rounded-lg bg-green-500/20 flex items-center justify-center flex-shrink-0">
                                <Sparkles className="h-6 w-6 text-green-400" />
                              </div>
                            </div>
                            
                            {/* Claim Button */}
                            <Button
                              onClick={() => {
                                if (dailyBonusCanClaim) {
                                  openDailyBonusModal();
                                }
                              }}
                              disabled={!dailyBonusCanClaim}
                              className={cn(
                                "w-full h-9 font-bold text-sm rounded-lg",
                                dailyBonusCanClaim
                                  ? "bg-gradient-to-r from-green-500 to-emerald-400 hover:from-green-400 hover:to-emerald-300 text-white"
                                  : "bg-muted text-muted-foreground cursor-default"
                              )}
                            >
                              {dailyBonusCanClaim ? (
                                'Claim Now'
                              ) : (
                                <span className="flex items-center gap-1.5">
                                  <span className="text-green-400">✓</span> Claimed
                                </span>
                              )}
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Promo Code Section - Collapsible */}
            {user && (
              <div className="bg-card border border-border rounded-xl overflow-hidden">
                {/* Collapsible Header */}
                <button
                      onClick={() => setShowPromoCodeSection(!showPromoCodeSection)}
                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-muted/60 transition-colors"
                >
                  <span className="text-sm font-semibold text-foreground flex items-center gap-2">
                    Have a Promo Code?
                    {appliedPromoCode && (
                      promoOffers.length > 0 ? (
                        <span className="text-xs text-green-400 font-medium">✓ {appliedPromoCode}</span>
                      ) : (
                        <span className="text-xs text-red-400 font-medium">✗ {appliedPromoCode} (no offers available)</span>
                      )
                    )}
                  </span>
                      {showPromoCodeSection ? (
                    <ChevronUp className="h-4 w-4 text-muted-foreground" />
                      ) : (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      )}
                </button>
                  
                {/* Expanded Content */}
                  {showPromoCodeSection && (
                  <div className="px-4 pb-4 border-t border-border">
                    {/* Input Row */}
                    <div className="flex flex-col sm:flex-row gap-4 pt-4">
                      <div className="flex-1 flex gap-2">
                        <input
                          type="text"
                          placeholder="Enter promo code"
                          value={promoCodeInput}
                          onChange={(e) => setPromoCodeInput(e.target.value.toUpperCase())}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && promoCodeInput.trim()) {
                              handleApplyPromoCode();
                            }
                          }}
                          className="flex-1 h-10 px-4 text-sm font-mono tracking-wider uppercase bg-background/70 border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20 transition-colors"
                        />
                        <Button
                          onClick={handleApplyPromoCode}
                          disabled={applyingPromoCode || !promoCodeInput.trim()}
                          className="h-10 px-6 bg-muted hover:bg-muted/80 text-foreground font-semibold text-sm rounded-lg disabled:opacity-40"
                        >
                          {applyingPromoCode ? '...' : 'Apply'}
                        </Button>
                      </div>
                      
                      {/* Helper text */}
                      <div className="text-xs text-muted-foreground/70 sm:max-w-[200px]">
                        <ul className="space-y-1">
                          <li>• Please note that all offers are valid as per applicable terms and conditions.</li>
                        </ul>
                      </div>
                    </div>
                    
                    {/* Promo Offers - Show when code is applied */}
                    {promoOffers.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-border">
                        <h3 className="text-sm font-bold text-amber-400 mb-3 flex items-center gap-2">
                          Your Promo Offers
                          <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-xs">
                            {promoOffers.length} available
                          </Badge>
                        </h3>
                        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {promoOffers.map((offer) => {
                      const totalGC = Number(offer.total_gc ?? offer.gc_amount ?? 0);
                      const totalSC = Number(offer.total_sc ?? offer.sc_amount ?? 0);
                      const displayPrice = offer.discounted_price !== undefined ? offer.discounted_price : offer.price;
                            const hasDiscount = offer.original_price && Number(offer.original_price) > Number(offer.price);
                      
                      return (
                        <Card
                          key={offer.id}
                                className="relative overflow-hidden transition-all duration-300 group bg-gradient-to-br from-amber-950/60 via-amber-900/40 to-amber-800/30 border border-amber-500/40 shadow-md hover:shadow-lg hover:shadow-amber-500/10"
                              >
                                {/* Top accent line - amber for promo */}
                                <div className="absolute top-0 left-0 right-0 h-0.5 opacity-80 group-hover:opacity-100 transition-opacity bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500" />

                                <CardContent className="p-4">
                                  {/* Currency display with badges - side by side */}
                                  <div className="flex items-center justify-center gap-3 mb-3 flex-wrap">
                                    {/* GC */}
                                    <div className="flex items-center gap-1.5">
                                      <GCBadgeSVG size={20} />
                                      <span className="text-base font-black text-amber-300">
                                        {formatCoins(totalGC)}
                                      </span>
                              </div>
                              
                                    {/* SC */}
                              {totalSC > 0 && (
                                <>
                                        <span className="text-muted-foreground/60">+</span>
                                        <div className="flex items-center gap-1.5">
                                          <SCBadgeSVG size={20} />
                                          <span className="text-sm font-bold text-green-400">
                                            {formatCoins(totalSC)} <span className="text-green-400/70 text-xs">FREE</span>
                                          </span>
                                      </div>
                                </>
                              )}
                          </div>
                          
                          <Button
                            onClick={() => handlePurchase({
                              id: offer.id,
                              title: offer.title,
                              price: offer.price,
                              original_price: offer.original_price,
                              gc_amount: Number(offer.gc_amount) || 0,
                              sc_amount: Number(offer.sc_amount) || 0,
                              gc_bonus_amount: Number(offer.gc_bonus_amount) || 0,
                              sc_bonus_amount: Number(offer.sc_bonus_amount) || 0,
                              type: 'promo_offer',
                              promo_code: offer.promo_code,
                              promo_reward_type: offer.promo_reward_type,
                              promo_reward_value: offer.promo_reward_value,
                              promo_bonus_gc: offer.promo_bonus_gc,
                              promo_bonus_sc: offer.promo_bonus_sc,
                              discounted_price: offer.discounted_price,
                            })}
                                    className="w-full font-bold px-4 py-2.5 rounded-lg text-sm h-10 transition-all duration-200 relative overflow-hidden group/btn bg-gradient-to-r from-amber-600 via-amber-500 to-amber-600 hover:from-amber-500 hover:via-amber-400 hover:to-amber-500 text-white shadow-md hover:shadow-lg border border-amber-500/50"
                                  >
                                    <span className="relative z-10 flex items-center justify-center gap-1.5">
                                      {hasDiscount && (
                                        <span className="line-through text-white/50 text-xs">
                                      {formatPrice(offer.original_price)}
                                    </span>
                              )}
                                      <span className="font-black">{formatPrice(displayPrice)}</span>
                            </span>
                                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-100%] group-hover/btn:translate-x-[100%] transition-transform duration-500" />
                          </Button>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
              </div>
            )}
                  </div>
                )}
              </div>
            )}

            {/* Divider */}
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-gradient-to-r from-transparent via-slate-700 to-transparent" />
              <span className="text-muted-foreground/70 text-xs font-medium uppercase tracking-wider">All Packages</span>
              <div className="flex-1 h-px bg-gradient-to-r from-transparent via-slate-700 to-transparent" />
                      </div>

            {/* Regular Packages Grid */}
            {regularPackages.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <GCBadgeSVG size={20} />
                    <h2 className="text-sm lg:text-base font-black text-foreground/80 uppercase tracking-wider">
                      <span className="lg:hidden">Packages</span>
                      <span className="hidden lg:inline">Gold Coin Packages</span>
                    </h2>
                    <span className="text-xs text-muted-foreground/70">({regularPackages.length})</span>
                  </div>
                  <Button 
                    variant="ghost" 
                    className="text-muted-foreground/70 hover:text-foreground/80 text-xs lg:text-sm h-8 px-3 rounded-full"
                    onClick={() => setShowSCInfoModal(true)}
                  >
                    What are FREE SCs? <span className="ml-1">ⓘ</span>
                  </Button>
                </div>
                
                {/* Desktop Grid */}
                <div className="hidden lg:grid lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {regularPackages.map((pkg, index) => (
                    <RegularPackageCard
                      key={pkg.id}
                      pkg={pkg}
                      index={index}
                      onPurchase={handlePurchase}
                      getPackageIcon={getPackageIcon}
                      formatCoins={formatCoins}
                      formatPrice={formatPrice}
                    />
                  ))}
                </div>
                
                {/* Mobile Grid - 2 columns */}
                <div className="lg:hidden grid grid-cols-2 gap-2.5">
                  {regularPackages.map((pkg, index) => (
                    <RegularPackageCard
                      key={pkg.id}
                      pkg={pkg}
                      index={index}
                      onPurchase={handlePurchase}
                      getPackageIcon={getPackageIcon}
                      formatCoins={formatCoins}
                      formatPrice={formatPrice}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* No packages available */}
            {packages.length === 0 && (
              <Card className="text-center py-12 bg-muted/30 border-border rounded-xl">
                <CardContent>
                  <div className="text-4xl mb-4">🎁</div>
                  <h3 className="text-lg font-bold mb-2 text-foreground/80">No Packages Available</h3>
                  <p className="text-sm text-muted-foreground/70">Check back later for exciting coin packages!</p>
                </CardContent>
              </Card>
            )}
            </>
            )}
          </div>
        </div>
      </div>

      {/* Sweepstakes Coins Info Modal */}
      <Dialog open={showSCInfoModal} onOpenChange={setShowSCInfoModal}>
        <DialogContent className="max-w-md bg-card border border-border p-0 overflow-hidden">
          {/* Header */}
          <div className="p-6 pb-4 border-b border-border">
            <h2 className="text-xl font-bold text-foreground">What are Sweepstakes Coins?</h2>
          </div>
          
          <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
            {/* Gold Coins Section */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <GCBadgeSVG size={20} />
                <h3 className="text-lg font-bold text-amber-400">Gold Coins</h3>
              </div>
              <ul className="space-y-2 text-sm text-foreground/80">
                <li className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                  <span>Play for free: No monetary value</span>
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                  <span>Daily rewards: Collect gold coins every day</span>
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                  <span>Gold coins cannot be redeemed for real cash prizes</span>
                </li>
              </ul>
            </div>

            {/* Sweepstakes Coins Section */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <SCBadgeSVG size={20} />
                <h3 className="text-lg font-bold text-green-400">Sweepstakes Coins</h3>
              </div>
              <ul className="space-y-2 text-sm text-foreground/80">
                <li className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Always free, no purchase necessary</span>
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Redeemable for real prizes</span>
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>1 SC redeemable for $1 USD</span>
                </li>
              </ul>
            </div>

            {/* How to get free SC */}
            <div className="bg-muted/50 rounded-xl p-4 border border-border space-y-4">
              <h3 className="text-base font-bold text-foreground">
                How do I get free <span className="text-green-400">Sweepstakes Coins</span>?
              </h3>
              <ul className="space-y-3 text-sm">
                <li className="flex items-center gap-3 text-foreground/80">
                  <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                    <LogIn className="h-4 w-4 text-amber-400" />
                  </div>
                  <span>Login Daily</span>
                </li>
                <li className="flex items-center gap-3 text-foreground/80">
                  <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                    <ThumbsUp className="h-4 w-4 text-blue-400" />
                  </div>
                  <span>Social Competitions</span>
                </li>
                <li className="flex items-center gap-3 text-foreground/80">
                  <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                    <Users className="h-4 w-4 text-purple-400" />
                  </div>
                  <span>Refer a friend</span>
                </li>
                <li className="flex items-center gap-3 text-foreground/80">
                  <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                    <Mail className="h-4 w-4 text-pink-400" />
                  </div>
                  <span>Mail-in Request</span>
                </li>
                <li className="flex items-center gap-3 text-foreground/80">
                  <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                    <ShoppingCart className="h-4 w-4 text-green-400" />
                  </div>
                  <span>Buy Gold Coins*</span>
                </li>
              </ul>
              <p className="text-xs text-muted-foreground/70 pt-2 border-t border-border">
                *Promotional free sweepstakes coins only available with selected gold coin packages. See full{' '}
                <a href="/sweeps-rules" className="text-amber-400 underline hover:text-amber-300">
                  sweepstakes rules here
                </a>.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
